/* eslint-disable @typescript-eslint/no-inferrable-types */
import React, {ChangeEventHandler, forwardRef, useContext} from 'react'

import {CheckboxGroupContext} from '@fuegokit/react/components/CheckboxGroup'
// import {useRefObjectAsForwardedRef, , } from '../..'
import {useRefObjectAsForwardedRef, useSafeLayoutEffect} from '@fuegokit/react/hooks'
import {CheckboxProps} from '@fuegokit/react/components/Checkbox'
import {StyledCheckbox} from './styles'

/**
 * Accessible, ecosystem-agnostic native checkbox primitive. Use checkboxes in forms when a user needs to select
 * multiple values from several options.
 */
const Checkbox = forwardRef(
  (
    {
      indeterminate,
      checked,
      disabled: disabledProp,
      required,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      onChange,
      name,
      sx: sxProp,
      value,
      ...props
    }: CheckboxProps,
    forwardedRef
  ) => {
    const innerRef = React.useRef<HTMLInputElement>(null)

    useRefObjectAsForwardedRef(forwardedRef, innerRef)

    const checkboxGroupContext = useContext(CheckboxGroupContext)
    const disabled = disabledProp || checkboxGroupContext.disabled
    const handleOnChange: ChangeEventHandler<HTMLInputElement> = e => {
      if (checkboxGroupContext.onChange) checkboxGroupContext.onChange(e)
      return onChange && onChange(e)
    }

    useSafeLayoutEffect(() => {
      if (innerRef.current) {
        innerRef.current.indeterminate = indeterminate || false
      }
    }, [indeterminate, checked, innerRef])

    let ariaChecked: 'mixed' | 'true' | 'false' | 'undefined'
    if (indeterminate) {
      ariaChecked = 'mixed'
    } else if (checked) {
      ariaChecked = 'true'
    } else {
      ariaChecked = 'false'
    }

    return (
      <StyledCheckbox
        type="checkbox"
        checked={indeterminate ? false : checked}
        aria-checked={ariaChecked}
        sx={sxProp}
        onChange={handleOnChange}
        ref={innerRef}
        disabled={disabled}
        aria-disabled={disabled ? 'true' : 'false'}
        required={required}
        aria-required={required ? 'true' : 'false'}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        value={value}
        name={name}
        {...props}
      />
    )
  }
)
Checkbox.displayName = 'Checkbox'
export default Checkbox
